import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import type { KlasseKey, RiskSummary } from "./types";
import type { Vereinbarung } from "./vereinbarungMock";

/**
 * Zusammenfassung der Beiträge für den Sammelvertrag-Zahlung-Step.
 * Summiert den Jahresgesamtbeitrag (Brutto) aus den Zuordnungen
 * (Versicherungsumfang) und leitet daraus automatisch die
 * Versicherungssteuer (Default 19 %) und den Netto-Betrag ab.
 */

const DEFAULT_STEUERSATZ = 19;

const parseSteuersatz = (raw: string | undefined): number => {
  if (!raw) return DEFAULT_STEUERSATZ;
  const n = parseFloat(raw.replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(n) && n >= 0 ? n : DEFAULT_STEUERSATZ;
};

const formatEur = (n: number): string =>
  `${n.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const formatPct = (n: number): string =>
  `${n.toLocaleString("de-DE", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} %`;

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
  },
  row: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    columnGap: "16px",
    paddingTop: "8px",
    paddingBottom: "8px",
    ...shorthands.borderBottom("1px", "dashed", BRAND.borderDashed),
  },
  rowLast: {
    ...shorthands.borderBottom("0"),
  },
  label: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    fontWeight: 700,
    color: BRAND.muted,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  },
  labelStrong: {
    color: BRAND.ink,
  },
  value: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.15rem",
    color: BRAND.ink,
    fontWeight: 600,
  },
  valueTotal: {
    fontSize: "1.6rem",
  },
  hint: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.82rem",
    color: BRAND.muted,
    fontStyle: "italic",
  },
});

type Props = {
  vereinbarung: Vereinbarung;
  vehicles: RiskSummary[];
  klasseByRisk: Record<number, KlasseKey | null>;
  /** riskId → assigned columnKey */
  assignment: Record<number, string>;
  /** Versicherungssteuersatz in % (z. B. "19" oder "19,00"). Default 19 %. */
  versicherungssteuersatz?: string;
};

const SammelvertragZahlungSummary = ({
  vereinbarung,
  vehicles,
  klasseByRisk,
  assignment,
  versicherungssteuersatz,
}: Props) => {
  const styles = useStyles();

  // Sum the Jahresgesamtbeitrag from all assigned vehicles.
  let totalBeitrag = 0;
  for (const v of vehicles) {
    const klasse = klasseByRisk[v.id];
    if (!klasse) continue;
    const colKey = assignment[v.id];
    if (!colKey) continue;
    const row = vereinbarung.rows.find((r) => r.klasse === klasse);
    const beitrag = row?.cells[colKey];
    if (typeof beitrag !== "number") continue;
    totalBeitrag += beitrag;
  }

  const steuerRate = parseSteuersatz(versicherungssteuersatz);
  const brutto = totalBeitrag;
  const netto = brutto / (1 + steuerRate / 100);
  const steuer = brutto - netto;

  return (
    <div className={styles.root}>
      <div className={styles.row}>
        <span className={mergeClasses(styles.label, styles.labelStrong)}>
          Jahresgesamtbeitrag (Brutto)
        </span>
        <span className={mergeClasses(styles.value, styles.valueTotal)}>
          {formatEur(brutto)}
        </span>
      </div>
      <div className={styles.row}>
        <span className={styles.label}>
          Versicherungssteuer ({formatPct(steuerRate)})
        </span>
        <span className={styles.value}>{formatEur(steuer)}</span>
      </div>
      <div className={mergeClasses(styles.row, styles.rowLast)}>
        <span className={styles.label}>Jahresgesamtbeitrag (Netto)</span>
        <span className={styles.value}>{formatEur(netto)}</span>
      </div>
      <span className={styles.hint}>
        Brutto-Beitrag aus dem Versicherungsumfang übernommen; Steuer und Netto
        werden automatisch berechnet.
      </span>
    </div>
  );
};

export default SammelvertragZahlungSummary;
